import { Component, OnInit } from '@angular/core';
import { ModalController, NavController, Platform, ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { SmallToCryptoPipe } from 'src/app/angular-wallet-base/pipes/smalltocrypto/smalltocrypto.pipe';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { AuthService } from 'src/app/angular-wallet-base/services/auth.service';
import { GraphService } from 'src/app/angular-wallet-base/services/graph.service';
import { Logger } from 'src/app/angular-wallet-base/services/logger.service';
import { MoralisService } from 'src/app/angular-wallet-base/services/moralis.service';
import { TokenManagerService } from 'src/app/angular-wallet-base/services/token-manager.service';
import { getSideMenuStatus } from 'src/app/angular-wallet-base/store/appSettings';
import { AppState } from 'src/app/angular-wallet-base/store/appState';
import { SubscriptionListenerComponent } from 'src/app/angular-wallet-base/SubscriptionListenerComponent';
import { presentToast } from 'src/app/angular-wallet-base/utils';
import { ERC20tokenData, TokenDefinition } from 'src/app/global';
import { ImportAssetModalComponent } from '../add-new-token-modal/add-new-token.modal';

@Component({
    selector: 'app-import-or-hide-token-modal',
    templateUrl: './import-or-hide-token.modal.html',
    styleUrls: ['./import-or-hide-token.modal.scss']
})
export class ImportOrHideAssetModalComponent extends SubscriptionListenerComponent implements OnInit {

    public isDesktopMode;
    public isImport: boolean = false;
    public importFromVault: boolean;
    public paragraphText: string;
    public title: string;
    public btnText1: string;
    public btnText2: string;
    public erc20TokenDetails: ERC20tokenData;
    public addTokenProgress = false;
    public hideTokenProgress = false;
    private addedNewToken = false;

    constructor(
        private modalController: ModalController,
        public appMode: AppModeService,
        protected store: Store<AppState>,
        private moralisService: MoralisService,
        private authService: AuthService,
        private tokenService: TokenManagerService,
        private translate: TranslateService,
        private toast: ToastController,
        private platform: Platform,
        private smallToCrypto: SmallToCryptoPipe,
    ) {
        super(store);
        this.subscriptions.add(
            this.appMode.isDesktopMode.subscribe(mode => this.isDesktopMode = mode)
        );
        this.subscriptions.add(
            this.tokenService.addTokenProgress.subscribe(async status => {
                this.addTokenProgress = status;
                // wait for new token to be added and then close modal
                if (this.addedNewToken && !this.addTokenProgress) {
                    await presentToast(
                        'Success',
                        'Token added successfully can be viewed shortly !!',
                        this.translate,
                        this.toast,
                        this.platform,
                        true
                    );
                    this.closeModal();
                }
            })
        );
        this.subscriptions.add(
            this.tokenService.hideTokenProgress.subscribe(async status => {
                this.hideTokenProgress = status;
            })
        );
    }

    async ngOnInit() {
        await this.authService.dismissModal('add-new-token-modal');
        if (this.isImport) {
            this.title = 'add_asset_vault_page.import_asset';
            this.paragraphText = 'import_or_hide_asset.import_these_assets';
            this.btnText1 = 'import_or_hide_asset.back';
            this.btnText2 = 'import_or_hide_asset.add_asset';
        } else {
            this.title = 'import_or_hide_asset.hide_asset';
            this.paragraphText = 'import_or_hide_asset.hide_these_assets';
            this.btnText1 = 'import_or_hide_asset.cancel';
            this.btnText2 = 'import_or_hide_asset.hide_asset';
        }
        this.erc20TokenDetails = this.moralisService.addERC20tokenData;
        if (!this.isImport) {
            this.erc20TokenDetails.balance = this.smallToCrypto.transform(
                this.erc20TokenDetails.balance,
                { type: this.erc20TokenDetails.baseChainSymbol, precision: this.erc20TokenDetails.precision }
            );
        }
        Logger.info('erc20TokenDetailsForImportAsset', this.erc20TokenDetails);
    }

    closeModal() {
        this.moralisService.setERC20tokenData(null, null);
        this.modalController.dismiss();
    }



    async back() {
        if (this.importFromVault){
            this.closeModal();
        } else {
            const sideMenuHidden = await this.store.select(getSideMenuStatus).pipe(take(1)).toPromise();
            let cssClass = '';
            cssClass += (this.isDesktopMode) ? 'add-new-token-modal-desktop' : 'add-new-token-modal';
            cssClass += (!sideMenuHidden && this.isDesktopMode) ? ' with-side-menu' : '';
            const modal = await this.modalController.create({
                component: ImportAssetModalComponent,
                cssClass,
                componentProps: {
                    tokensForImportERC20token: this.erc20TokenDetails
                },
                initialBreakpoint: (this.isDesktopMode) ? undefined : 0.6,
                breakpoints: (this.isDesktopMode) ? undefined : [0, 0.6, 0.7, 1],
            });
            await modal.present();
        }
    }

    async importNewToken() {
        const tokenName = `${this.erc20TokenDetails.baseChainSymbol}-${this.erc20TokenDetails.assetGuid}`;
        const newTokenPayload: { [key: string]: TokenDefinition } = {
            [tokenName]: {
                chainId: this.erc20TokenDetails.chainId,
                assetGuid: this.erc20TokenDetails.assetGuid,
                owner_address: '',
                symbol: this.erc20TokenDetails.symbol,
                name: this.erc20TokenDetails.name,
                baseChainSymbol: this.erc20TokenDetails.baseChainSymbol,
                logo: this.erc20TokenDetails.logo,
                coingeckoId: this.erc20TokenDetails.coingeckoId,
                disabled: false,
                precision: +this.erc20TokenDetails.precision
            }
        };

        Logger.info('Adding new token', newTokenPayload);
        const addNewToken = await this.tokenService.addNewToken(newTokenPayload, this.erc20TokenDetails.assetGuid);
        if (addNewToken) {
            this.addedNewToken = true;
        } else {
            await presentToast(
                'Failed',
                'Please try again later',
                this.translate,
                this.toast,
                this.platform,
                true
            );
            this.closeModal();
        }
    }

    async hideToken() {
        const hideTokenStatus = await this.tokenService.hideErc20Token(this.erc20TokenDetails);
        if (hideTokenStatus) {
            await presentToast(
                'Success',
                'Token hidden successfully !!',
                this.translate,
                this.toast,
                this.platform,
                true
            );
            this.closeModal();
        } else {
            await presentToast(
                'Failed',
                'Please try again later',
                this.translate,
                this.toast,
                this.platform,
                true
            );
            this.closeModal();
        }
    }

}
